import type { FC } from 'react';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { ErrorMessage } from '@hookform/error-message';
import { createSelector } from '@reduxjs/toolkit';
import cn from 'classnames';
import { useDispatch, useSelector } from 'react-redux';

import { _isNotBlank } from '@/littledash';

import { featuresSelector } from '@/support/Selectors';

import ReasonForChange from '@/components/Shared/Latest/ReasonForChange';
import Checkbox from '@/components/UI/FormElements/Checkbox';

import {
  CASCADE_DELETE_CHILD_OPTIONS,
  UNCHECKED_CASCADE_DELETE_OPTIONS,
  type CascadeDeleteChildType,
  type CascadeDeleteOptions,
} from './ConfirmDelete.model';

import styles from './ConfirmDelete.module.scss';

export type { CascadeDeleteOptions };

interface ConfirmDeleteProps {
  onClick: (reason?: string, cascade?: CascadeDeleteOptions) => void;
  title: string;
  text: string;
  btnTxt: string;
  textOverride?: string;
  showReasonForChange?: boolean;
  showCascadeDeleteOptions?: boolean;
}

interface FormDataProps {
  confirm: string;
  reason_for_change: string;
  cascade: CascadeDeleteOptions;
}

const featureSelector = createSelector([featuresSelector], (features) => ({
  optInCascadeDelete: features?.opt_in_cascade_delete ?? false,
  reasonForChange: features?.reason_for_change ?? false,
}));

const ConfirmDelete: FC<ConfirmDeleteProps> = ({
  onClick,
  title,
  text,
  btnTxt,
  textOverride,
  showReasonForChange,
  showCascadeDeleteOptions,
}) => {
  const { optInCascadeDelete, reasonForChange } = useSelector(featureSelector);
  const dispatch = useDispatch();

  const formMethods = useForm<FormDataProps>({
    defaultValues: {
      confirm: '',
      reason_for_change: '',
      cascade: { ...UNCHECKED_CASCADE_DELETE_OPTIONS },
    },
  });

  const {
    control,
    handleSubmit,
    formState: { errors },
    register,
    setValue,
  } = formMethods;

  const cascade = useWatch({ control, name: 'cascade' }) ?? { ...UNCHECKED_CASCADE_DELETE_OPTIONS };
  const hasSelectedCascadeOption = CASCADE_DELETE_CHILD_OPTIONS.some(([name]) => cascade[name] === true);
  const shouldShowCascadeDeleteOptions = optInCascadeDelete === true && showCascadeDeleteOptions === true;

  const closeModal = () => {
    dispatch({
      type: 'CLOSE_MODAL',
    });
  };

  const setCascadeOption = (name: CascadeDeleteChildType, checked: boolean) => {
    setValue(`cascade.${name}`, checked, { shouldDirty: true });
  };

  const setAllCascadeOptions = (checked: boolean) => {
    CASCADE_DELETE_CHILD_OPTIONS.forEach(([name]) => {
      setCascadeOption(name, checked);
    });
  };

  const onSubmit = ({ reason_for_change, cascade: cascadeOptions }: FormDataProps) => {
    const shouldPassReason = reasonForChange === true && showReasonForChange === true;
    const reason = shouldPassReason ? reason_for_change : undefined;
    if (shouldShowCascadeDeleteOptions) {
      onClick(reason, cascadeOptions);
    } else {
      onClick(reason);
    }
    closeModal();
  };

  return (
    <FormProvider {...formMethods}>
      <form
        onSubmit={handleSubmit(onSubmit)}
        className={cn('ui__narrow-modal', { [styles.narrowModalWide]: shouldShowCascadeDeleteOptions })}
        data-testid="confirm-delete__modal"
      >
        <div className="pb3 tc">
          <h3 className="normal f4 pb3" data-testid="confirm-delete__modal-header">
            {title}
          </h3>
          <p className="f6 pb2" data-testid="confirm-delete__modal-subheader">
            {_isNotBlank(textOverride) ? textOverride : text}
          </p>
        </div>
        {shouldShowCascadeDeleteOptions && (
          <>
            <div className="bb b--gray mb3"></div>
            <div className="pb3" data-test-element="cascade-delete-options">
              <h4 className="f5 fw5 pb1">Also delete child items</h4>
              <p className="f6 dark-gray lh-copy pb3">
                Optionally delete child items with the animal. Webhooks will be sent for selected item types.
              </p>
              <div className="flex flex-wrap items-center pb1">
                <a
                  className="dib link blue f7 mr3"
                  onClick={() => setAllCascadeOptions(true)}
                  data-testid="confirm-delete__cascade-select-all"
                >
                  Select all
                </a>
                <a
                  className={cn('dib f7', {
                    'link blue': hasSelectedCascadeOption,
                    'pointer-events-none': !hasSelectedCascadeOption,
                  })}
                  onClick={() => setAllCascadeOptions(false)}
                  data-testid="confirm-delete__cascade-clear"
                >
                  Clear selection
                </a>
              </div>
              <div className={cn('pt3', 'flex', 'flex-wrap')} data-test-element="cascade-delete-item-options">
                {CASCADE_DELETE_CHILD_OPTIONS.map(([name, label]) => (
                  <div className="w-50 pb2" key={name}>
                    <Checkbox
                      id={`cascade-${name}`}
                      name={`cascade.${name}`}
                      label={label}
                      labelClass="pl2"
                      testPrefix={`confirm-delete__cascade-${name}`}
                      checked={cascade[name] === true}
                      onChange={(event) => setCascadeOption(name, event.target.checked)}
                    />
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
        <div className="pb3">
          {reasonForChange === true && showReasonForChange === true ? (
            <ReasonForChange data-testid="confirm-delete__modal-reason-for-change" isDelete />
          ) : (
            <>
              <label htmlFor="confirm" className="lh-copy" data-testid="confirm-delete__modal-label">
                Type DELETE into the field to confirm
              </label>
              <input
                type="text"
                id="confirm"
                data-testid="confirm-delete__modal-input"
                {...register('confirm', { validate: (value: string) => value === 'DELETE' })}
              />
              <ErrorMessage
                errors={errors}
                name="confirm"
                render={() => (
                  <p className="f6 red db lh-copy" data-testid="confirm-delete__modal-validation">
                    Text entered is incorrect, enter DELETE to continue.
                  </p>
                )}
              />
            </>
          )}
        </div>
        <div>
          <button type="submit" className="button critical w-100" data-testid="confirm-delete__modal-confirm">
            {btnTxt}
          </button>
          <button
            type="button"
            className="button plain db w-100 mt2"
            onClick={closeModal}
            data-testid="confirm-delete__modal-cancel"
          >
            Cancel
          </button>
        </div>
      </form>
    </FormProvider>
  );
};

export default ConfirmDelete;
